class FlavormaxxSubscription extends HTMLElement {
  constructor() {
    super();

    this.SELECTORS = {
      addToCartBtn: '.js-add-to-cart-btn',
      productBoxOption: '.js-product-box-option',
      flavorJson: '.js-flavor-json',
      configJson: '.js-flavormaxx-config',
      customizeBtn: '.js-flavormaxx-customize-btn',
      rowTemplate: '.js-flavormaxx-row-template',
      rowsContainer: '.js-flavormaxx-rows',
      row: '.js-flavormaxx-row',
      rowLabel: '.js-flavormaxx-row-label',
      rowDescription: '.js-flavormaxx-row-description',
      removeRowBtn: '.js-flavormaxx-remove-row',
      flavorItem: '.js-flavormaxx-flavor-item',
      cartDrawer: 'cart-drawer',
      freeGiftMessage: '.js-free-gift-message',
    };

    this.CLASSES = {
      active: 'm-active',
      selected: 'm-selected',
      loading: 'm-loading',
      hidden: 'm-hidden',
      entering: 'm-entering',
      leaving: 'm-leaving',
    };

    this.loadingText = 'Mixing Your Magna...';

    this.endpoints = {
      cart: '/cart/add.js',
      checkout: '/cart/checkout',
    };

    this.selectedBox = null;
    this.selectedProductBoxOption = null;
    this.productFlavorsData = [];
    this.boxConfig = {};
    this.boxRows = {};
    this.rowIdCounter = 0;

    this.isNewSession = false;
    this.subscriptionToCheckout = false;
  }

  connectedCallback() {
    this.init();
    this.events();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  init() {
    // Parse box config from embedded JSON
    this.parseBoxConfig();

    this.selectedProductBoxOption = [...this.querySelectorAll(this.SELECTORS.productBoxOption)].find((option) =>
      option.classList.contains(this.CLASSES.active)
    );

    if (this.selectedProductBoxOption) {
      this.selectedBox = this.selectedProductBoxOption.getAttribute('data-box');
    }

    this.productFlavorsData = this.parseFlavorJson();
    this.initBoxRows();
    this.trackSessionAndVisits();
    this.updateAllPriceDisplays();
    this.initServingsTemplates();
    this.updateAllServingsDisplays();
    this.updateAllFlavorCountDisplays();
    this.updateAddToCartButton();
    this.updateFreeGiftVisibility();
  }

  parseBoxConfig() {
    const $config = this.querySelector(this.SELECTORS.configJson);
    if (!$config) return;

    try {
      const config = JSON.parse($config.innerHTML);
      this.boxConfig = {};
      config.boxes.forEach((box) => {
        this.boxConfig[box.id] = box;
        this.boxRows[box.id] = [];
      });
    } catch (e) {
      console.error('Failed to parse flavormaxx config:', e.message);
    }
  }

  initBoxRows() {
    Object.keys(this.boxConfig).forEach((box) => {
      const $container = this.getRowsContainer(box);
      if (!$container) return;

      const $rows = [...$container.querySelectorAll(this.SELECTORS.row)];
      this.boxRows[box] = [];

      $rows.forEach(($row) => {
        this.syncRowStateFromDom(box, $row);
      });

      const min = this.getMinForBox(box);
      while (this.getRowsForBox(box).length < min) {
        this.appendRow(box, { locked: true });
      }

      this.updateRowLabels(box);
    });
  }

  syncRowStateFromDom(box, $row) {
    const rowId = $row.dataset.rowId || this.createRowId(box);
    const locked = $row.dataset.locked === 'true';
    const $flavorItems = [...$row.querySelectorAll(this.SELECTORS.flavorItem)];
    if (!$flavorItems.length) return;

    const $selectedFlavor =
      $flavorItems.find(($item) => $item.getAttribute('data-variant-selected') === 'true') || $flavorItems[0];

    this.updateRowSelectionUI($row, $selectedFlavor.dataset.id);
    this.updateRowDescription($row, $selectedFlavor);

    this.boxRows[box].push({
      rowId,
      productId: $selectedFlavor.dataset.productId,
      variantId: $selectedFlavor.dataset.id,
      locked,
    });

    $row.dataset.rowId = rowId;
    $row.dataset.locked = locked ? 'true' : 'false';
    $row.dataset.removable = locked ? 'false' : 'true';
  }

  events() {
    this._handleClick = (event) => {
      const $addToCartBtn = event.target.closest(this.SELECTORS.addToCartBtn);
      const $customizeBtn = event.target.closest(this.SELECTORS.customizeBtn);
      const $removeRowBtn = event.target.closest(this.SELECTORS.removeRowBtn);
      const $flavorItem = event.target.closest(this.SELECTORS.flavorItem);
      const $productBoxOption = event.target.closest(this.SELECTORS.productBoxOption);

      if ($addToCartBtn) {
        this.handleAddToCartClick(event, $addToCartBtn);
        return;
      }
      if ($customizeBtn) {
        event.stopPropagation();
        this.handleCustomizeLinkClick(event, $customizeBtn);
        return;
      }
      if ($removeRowBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.handleRemoveRowClick(event, $removeRowBtn);
        return;
      }
      if ($flavorItem) {
        event.preventDefault();
        event.stopPropagation();
        this.handleFlavorItemClick(event, $flavorItem);
        return;
      }
      if ($productBoxOption) {
        if (event.target.closest('.ctr-flavormaxx__customize-area')) return;
        this.handleProductBoxOptionClick(event, $productBoxOption);
        return;
      }
    };

    this.addEventListener('click', this._handleClick);
  }

  // --- Box Option Selection ---

  handleProductBoxOptionClick(event, $productBoxOption) {
    if (event) event.preventDefault();
    const newBox = $productBoxOption.getAttribute('data-box');
    if (newBox === this.selectedBox) return;

    this.selectedBox = newBox;
    this.selectedProductBoxOption = $productBoxOption;

    this.querySelectorAll(this.SELECTORS.productBoxOption).forEach((option) => {
      option.classList.remove(this.CLASSES.active);
    });
    $productBoxOption.classList.add(this.CLASSES.active);

    this.updateAddToCartButton();
    this.updatePriceDisplay(newBox);
    this.updateServingsDisplay(newBox);
    this.updateFreeGiftVisibility();
    this.syncGalleryToBox(newBox);
  }

  /** Main gallery follows the first flavor row for this box (matches cart / servings). */
  syncGalleryToBox(box) {
    const rows = this.getRowsForBox(box);
    const first = rows[0];
    if (!first?.productId) return;
    this.syncGalleryToFlavor(first.productId);
  }

  handleCustomizeLinkClick(event, $customizeBtn) {
    if (event) event.preventDefault();
    const box = $customizeBtn.dataset.box;
    this.appendRow(box);
    this.updateValidation(box);
  }

  handleFlavorItemClick(event, $flavorItem) {
    if (event) event.preventDefault();

    const $row = $flavorItem.closest(this.SELECTORS.row);
    if (!$row) return;

    const box = $row.dataset.box;
    const rowId = $row.dataset.rowId;
    const rowState = this.getRowState(box, rowId);
    if (!rowState) return;

    rowState.productId = $flavorItem.dataset.productId;
    rowState.variantId = $flavorItem.dataset.id;

    this.updateRowSelectionUI($row, rowState.variantId);
    this.updateRowDescription($row, $flavorItem);
    this.updateServingsDisplay(box);
    this.syncGalleryToFlavor($flavorItem.dataset.productId);
  }

  syncGalleryToFlavor(productId) {
    const flavor = this.productFlavorsData.find((f) => String(f.id) === String(productId));
    const productBox = this.closest('.ctr-product-box');
    const media = flavor?.media;
    if (
      !flavor ||
      !productBox ||
      typeof window.magna?.updateFlavormaxxGallery !== 'function' ||
      !Array.isArray(media) ||
      media.length === 0
    ) {
      return;
    }
    window.magna.updateFlavormaxxGallery(productBox, media, flavor.product?.title || '');
  }

  handleRemoveRowClick(event, $removeRowBtn) {
    if (event) event.preventDefault();

    const box = $removeRowBtn.dataset.box;
    const rowId = $removeRowBtn.dataset.rowId;
    const rowState = this.getRowState(box, rowId);

    if (!rowState || rowState.locked) return;

    this.boxRows[box] = this.getRowsForBox(box).filter((row) => row.rowId !== rowId);

    const $row = this.querySelector(`${this.SELECTORS.row}[data-row-id="${rowId}"]`);
    if (!$row) {
      this.updateRowLabels(box);
      this.updateValidation(box);
      return;
    }

    this.updateValidation(box);
    this.animateRowOut($row, () => {
      $row.remove();
      this.updateRowLabels(box);
    });
  }

  appendRow(box, { locked = false } = {}) {
    const $container = this.getRowsContainer(box);
    const $template = this.querySelector(this.SELECTORS.rowTemplate);
    const defaultFlavor = this.getDefaultFlavor();

    if (!$container || !$template || !defaultFlavor) return null;

    const rowId = this.createRowId(box);
    const rowNumber = this.getRowsForBox(box).length + 1;
    const html = this.renderRowTemplate($template.innerHTML, {
      boxId: box,
      rowId,
      rowNumber: String(rowNumber),
    });

    $container.insertAdjacentHTML('beforeend', html);

    const $row = $container.querySelector(`${this.SELECTORS.row}[data-row-id="${rowId}"]`);
    if (!$row) return null;

    if (locked) {
      const $removeBtn = $row.querySelector(this.SELECTORS.removeRowBtn);
      if ($removeBtn) $removeBtn.remove();
    }

    $row.dataset.locked = locked ? 'true' : 'false';
    $row.dataset.removable = locked ? 'false' : 'true';
    this.updateRowSelectionUI($row, defaultFlavor.firstVariantId);

    const $defaultFlavorItem = $row.querySelector(`${this.SELECTORS.flavorItem}[data-id="${defaultFlavor.firstVariantId}"]`)
    if ($defaultFlavorItem) this.updateRowDescription($row, $defaultFlavorItem)

    this.getRowsForBox(box).push({
      rowId,
      productId: String(defaultFlavor.id),
      variantId: String(defaultFlavor.firstVariantId),
      locked,
    });

    this.updateRowLabels(box);
    this.animateRowIn($row);
    return $row;
  }

  renderRowTemplate(template, { boxId, rowId, rowNumber }) {
    return template
      .split('__BOX_ID__').join(boxId)
      .split('__ROW_ID__').join(rowId)
      .split('__ROW_NUMBER__').join(rowNumber);
  }

  updateRowSelectionUI($row, selectedVariantId) {
    $row.querySelectorAll(this.SELECTORS.flavorItem).forEach(($item) => {
      const isSelected = String($item.dataset.id) === String(selectedVariantId);
      $item.classList.toggle(this.CLASSES.selected, isSelected);
      $item.setAttribute('data-variant-selected', isSelected ? 'true' : 'false');
    });
  }

  updateRowDescription($row, $flavorItem) {
    const $desc = $row.querySelector(this.SELECTORS.rowDescription)
    if (!$desc) return
    const description = $flavorItem.dataset.flavorDescription || ''
    const displayName = $flavorItem.dataset.flavorDisplayName || ''
    if (description) {
      $desc.textContent = `${displayName} \u2014 ${description}`
    } else {
      $desc.textContent = ''
    }
  }

  getOrdinalWord(n) {
    const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth']
    return ordinals[n - 1] || `#${n}`
  }

  updateRowLabels(box) {
    const $container = this.getRowsContainer(box)
    if (!$container) return

    ;[...$container.querySelectorAll(this.SELECTORS.row)].forEach(($row, index) => {
      const rowNumber = index + 1
      const ordinal = this.getOrdinalWord(rowNumber)
      const $label = $row.querySelector(this.SELECTORS.rowLabel)
      const $removeBtn = $row.querySelector(this.SELECTORS.removeRowBtn)

      if ($label) $label.textContent = `Select ${ordinal} Flavor \u2014 14 Sticks`
      if ($removeBtn) $removeBtn.setAttribute('aria-label', `Remove ${ordinal} Flavor`)
    })
  }

  animateRowIn($row) {
    if (!$row) return;

    const targetHeight = $row.scrollHeight;
    $row.classList.add(this.CLASSES.entering);
    $row.style.height = '0px';
    $row.style.opacity = '0';
    $row.style.transform = 'translateY(-10px)';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        $row.style.height = `${targetHeight}px`;
        $row.style.opacity = '1';
        $row.style.transform = 'translateY(0)';
      });
    });

    const cleanup = (event) => {
      if (event.target !== $row || event.propertyName !== 'height') return;
      $row.classList.remove(this.CLASSES.entering);
      $row.style.height = '';
      $row.style.opacity = '';
      $row.style.transform = '';
      $row.removeEventListener('transitionend', cleanup);
    };

    $row.addEventListener('transitionend', cleanup);
  }

  animateRowOut($row, onComplete = () => {}) {
    if (!$row) return;

    $row.classList.add(this.CLASSES.leaving);
    $row.style.height = `${$row.offsetHeight}px`;
    $row.style.opacity = '1';
    $row.style.transform = 'translateY(0)';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        $row.style.height = '0px';
        $row.style.opacity = '0';
        $row.style.transform = 'translateY(-10px)';
      });
    });

    const cleanup = (event) => {
      if (event.target !== $row || event.propertyName !== 'height') return;
      $row.removeEventListener('transitionend', cleanup);
      onComplete();
    };

    $row.addEventListener('transitionend', cleanup);
  }

  createRowId(box) {
    this.rowIdCounter += 1;
    return `${box}-row-${this.rowIdCounter}`;
  }

  // --- Validation ---

  getTotalForBox(box) {
    return this.getRowsForBox(box).length;
  }

  getMinForBox(box) {
    const config = this.boxConfig[box];
    return config ? config.minFlavors : 2;
  }

  getSellingPlanForBox(box) {
    const config = this.boxConfig[box];
    return config ? config.sellingPlanId : null;
  }

  canBoxSubscribe(box) {
    const config = this.boxConfig[box];
    return config ? config.canSubscribe : false;
  }

  // --- Tiered Pricing ---

  getPricingForBox(boxId, qty) {
    const config = this.boxConfig[boxId]
    if (!config) return null

    const basePrice = config.basePrice || null

    // Filter out empty tier objects (from Liquid trailing comma workaround)
    const tiers = (config.pricingTiers || []).filter((t) => t.minQty != null)
    if (tiers.length === 0) {
      const total = (basePrice || 0) * qty
      return { price: total, oldPrice: null, savingsPct: 0 }
    }

    // Find the highest tier where qty >= minQty
    const sorted = [...tiers].sort((a, b) => b.minQty - a.minQty)
    const tier = sorted.find((t) => qty >= t.minQty) || tiers[0]

    // Calculate totals (price × quantity)
    const totalPrice = tier.price * qty
    const totalOldPrice = basePrice ? basePrice * qty : null
    const oldPrice = totalOldPrice && totalOldPrice > totalPrice ? totalOldPrice : null
    const savingsPct = oldPrice ? Math.round(((oldPrice - totalPrice) / oldPrice) * 100) : 0

    return { price: totalPrice, oldPrice, savingsPct }
  }

  updatePriceDisplay(boxId) {
    const box = boxId || this.selectedBox
    if (!box) return

    const config = this.boxConfig[box]
    if (!config) return

    const min = this.getMinForBox(box)
    const actualQty = this.getTotalForBox(box)
    const qty = Math.max(actualQty, min)
    const pricing = this.getPricingForBox(box, qty)
    if (!pricing) return

    const $container = this.querySelector(`.js-box-price-container[data-box-id="${box}"]`)
    if (!$container) return

    const $priceNew = $container.querySelector('.js-box-price-new')
    const $priceOld = $container.querySelector('.js-box-price-old')
    const $savings = $container.querySelector('.js-box-savings')

    if ($priceNew) {
      if (pricing.price && pricing.price !== 0) {
        $priceNew.textContent = `$${this.formatPrice(pricing.price)}`
        $priceNew.style.display = ''
      } else {
        $priceNew.style.display = 'none'
      }
    }

    if ($priceOld) {
      if (pricing.oldPrice && pricing.oldPrice !== 0) {
        $priceOld.textContent = `$${this.formatPrice(pricing.oldPrice)}`
        $priceOld.style.display = ''
      } else {
        $priceOld.style.display = 'none'
      }
    }

    if ($savings) {
      if (pricing.savingsPct && pricing.savingsPct !== 0) {
        $savings.textContent = `Save ${pricing.savingsPct}%`
        $savings.style.display = ''
      } else {
        $savings.style.display = 'none'
      }
    }

    // Grey out price when customized but minimum not met
    if (actualQty < min) {
      $container.classList.add('m-price-greyed')
    } else {
      $container.classList.remove('m-price-greyed')
    }
  }

  updateAllPriceDisplays() {
    Object.keys(this.boxConfig).forEach((boxId) => {
      this.updatePriceDisplay(boxId)
    })
  }

  // --- Dynamic Servings ---

  initServingsTemplates() {
    this.servingsTemplates = {}
    this.querySelectorAll('[data-box-description]').forEach(($list) => {
      const boxId = $list.dataset.boxDescription
      this.servingsTemplates[boxId] = $list.innerHTML
    })
  }

  getFlavorCountForBox(box) {
    const rows = this.getRowsForBox(box)
    const uniqueProductIds = [...new Set(rows.map((row) => String(row.productId)).filter(Boolean))]
    let flavorCount = 0
    for (const productId of uniqueProductIds) {
      const flavorData = this.productFlavorsData.find((f) => String(f.id) === String(productId))
      const title = flavorData && flavorData.product ? flavorData.product.title.toLowerCase() : ''
      if (title.includes('variety') || title.includes('sampler')) {
        flavorCount += 7
      } else {
        flavorCount += 1
      }
    }
    return Math.min(flavorCount, 7)
  }

  updateServingsDisplay(boxId) {
    const box = boxId || this.selectedBox
    if (!box || !this.servingsTemplates[box]) return

    const min = this.getMinForBox(box)
    const actualQty = this.getTotalForBox(box)
    const qty = Math.max(actualQty, min)
    const servings = qty * 14

    // Update description list
    const $list = this.querySelector(`[data-box-description="${box}"]`)
    if ($list) {
      $list.innerHTML = this.servingsTemplates[box].replace(/\{servings\}/g, servings)
    }
  }

  updateAllServingsDisplays() {
    Object.keys(this.boxConfig).forEach((boxId) => {
      this.updateServingsDisplay(boxId)
    })
  }

  // --- Dynamic Flavor Count in Option Name ---

  updateFlavorCountDisplay(boxId) {
    const box = boxId || this.selectedBox
    if (!box) return
    const $el = this.querySelector(`[data-box-option-name="${box}"]`)
    if (!$el) return
    const template = $el.dataset.nameTemplate || ''
    if (!template.includes('[count]')) return
    const count = Math.max(this.getTotalForBox(box), this.getMinForBox(box))
    const $icon = $el.querySelector('.ctr-product-subscription__option-name-icon')
    const iconHTML = $icon ? $icon.outerHTML : ''
    $el.innerHTML = iconHTML + template.replace(/\[count\]/g, count)
  }

  updateAllFlavorCountDisplays() {
    Object.keys(this.boxConfig).forEach((boxId) => {
      this.updateFlavorCountDisplay(boxId)
    })
  }

  formatPrice(value) {
    const num = Number(value)
    return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2)
  }


  updateValidation(boxId = this.selectedBox) {
    this.updateAddToCartButton();
    this.updatePriceDisplay(boxId);
    this.updateServingsDisplay(boxId);
    this.updateFlavorCountDisplay(boxId);
  }

  // --- Add to Cart ---

  async handleAddToCartClick(event, $addToCartBtn) {
    if (event) event.preventDefault();

    const box = this.selectedBox;
    const total = this.getTotalForBox(box);
    const min = this.getMinForBox(box);
    if (total < min) return;

    const oldBtnText = $addToCartBtn.innerText;
    try {
      let redirectTo = this.addToCartType;
      const canSubscribe = this.canBoxSubscribe(box);

      if (canSubscribe && this.subscriptionToCheckout) {
        redirectTo = 'checkout';
      }

      $addToCartBtn.classList.add(this.CLASSES.loading);
      $addToCartBtn.setAttribute('disabled', 'true');
      $addToCartBtn.innerText = this.loadingText;

      const $cartDrawer = document.querySelector(this.SELECTORS.cartDrawer);
      if (!$cartDrawer) {
        throw new Error('Cart drawer not found');
      }

      const sections = $cartDrawer.getSectionsToRender().map((section) => section.id);
      const items = this.getItems();

      if (redirectTo === 'checkout') {
        await window.clearCart();
      }

      const response = await fetch(this.endpoints.cart, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections }),
      });

      if (!response.ok) {
        throw new Error('There was a problem with the request');
      }

      if (window.location.host.includes('drinkmagna')) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'add_to_cart', items });
      }

      const json = await response.json();

      if (redirectTo === 'drawer') {
        $cartDrawer.renderContents(json);
      } else {
        document.location.href = this.endpoints.checkout;
      }
    } catch (e) {
      console.error(e.message);
    } finally {
      $addToCartBtn.innerText = oldBtnText;
      $addToCartBtn.classList.remove(this.CLASSES.loading);
      $addToCartBtn.removeAttribute('disabled');
    }
  }

  getItems() {
    const box = this.selectedBox;
    const canSubscribe = this.canBoxSubscribe(box);
    const sellingPlanId = this.getSellingPlanForBox(box);
    const items = [];
    const aggregatedItems = new Map();

    this.getRowsForBox(box).forEach((row) => {
      if (!row.variantId) return;

      const key = String(row.variantId);
      if (!aggregatedItems.has(key)) {
        const item = {
          id: Number(row.variantId),
          quantity: 0,
        };

        if (canSubscribe && sellingPlanId) {
          item.selling_plan = sellingPlanId;
        }

        if (this.offerPropertyValue) {
          item.properties = { _offer: this.offerPropertyValue };
        }

        aggregatedItems.set(key, item);
      }

      aggregatedItems.get(key).quantity += 1;
    });

    items.push(...aggregatedItems.values());

    // Append gift products for the selected box
    const boxCfg = this.boxConfig[box];
    if (boxCfg && Array.isArray(boxCfg.giftProducts)) {
      boxCfg.giftProducts.forEach((gift) => {
        if (gift && gift.variantId) {
          items.push({
            id: gift.variantId,
            quantity: gift.quantity || 1,
          });
        }
      });
    }

    return items;
  }

  // --- Button State ---

  updateAddToCartButton() {
    const $btn = this.querySelector(this.SELECTORS.addToCartBtn);
    if (!$btn) return;

    const box = this.selectedBox;
    const total = this.getTotalForBox(box);
    const min = this.getMinForBox(box);

    if (total < min) {
      const remaining = min - total;
      $btn.setAttribute('disabled', 'true');
      $btn.innerText = `Select ${remaining} more flavor${remaining !== 1 ? 's' : ''}`;
      return;
    }

    $btn.removeAttribute('disabled');
    const canSubscribe = this.canBoxSubscribe(box);

    if (this.buttonBehavior === 'default') {
      if (!canSubscribe) {
        $btn.innerText = this.oneTimeButtonText;
      } else {
        $btn.innerText = this.cartDrawerButtonText;
      }
      return;
    }

    // Mix behavior
    if (!canSubscribe) {
      $btn.innerText = this.cartDrawerButtonText;
      this.subscriptionToCheckout = false;
    } else if (this.isNewSession) {
      $btn.innerText = this.checkoutButtonText;
      this.subscriptionToCheckout = true;
    } else {
      $btn.innerText = this.cartDrawerButtonText;
      this.subscriptionToCheckout = false;
    }
  }

  updateFreeGiftVisibility() {
    const $gifts = this.querySelectorAll(this.SELECTORS.freeGiftMessage)
    if (!$gifts.length) return

    const canSubscribe = this.canBoxSubscribe(this.selectedBox)
    $gifts.forEach(($gift) => {
      $gift.style.display = canSubscribe ? '' : 'none'
    })
  }

  // --- Session Tracking ---

  trackSessionAndVisits() {
    let totalVisits = parseInt(localStorage.getItem('magnaVisitCount')) || 0;
    const isNewSession = !sessionStorage.getItem('magnaSessionStarted');

    localStorage.setItem('magnaVisitCount', ++totalVisits);

    if (isNewSession) {
      sessionStorage.setItem('magnaSessionStarted', 'true');
      this.isNewSession = true;
    }
  }

  // --- Parsing ---

  parseFlavorJson() {
    try {
      return [...this.querySelectorAll(this.SELECTORS.flavorJson)].map((el) => JSON.parse(el.innerHTML));
    } catch (e) {
      console.error(e.message);
      return [];
    }
  }

  // --- Getters ---

  get addToCartType() {
    return this.getAttribute('add-to-cart-type') || 'drawer';
  }

  get offerPropertyValue() {
    return this.getAttribute('offer-property-value');
  }

  get cartDrawerButtonText() {
    return this.getAttribute('cart-drawer-button-text');
  }

  get checkoutButtonText() {
    return this.getAttribute('checkout-button-text');
  }

  get buttonBehavior() {
    return this.getAttribute('button-behavior') || 'default';
  }

  get oneTimeButtonText() {
    return this.getAttribute('one-time-button-text') || this.cartDrawerButtonText;
  }

  getDefaultFlavor() {
    return this.productFlavorsData[0] || null;
  }

  getRowsForBox(box) {
    return this.boxRows[box] || [];
  }

  getRowState(box, rowId) {
    return this.getRowsForBox(box).find((row) => row.rowId === rowId) || null;
  }

  getRowsContainer(box) {
    return this.querySelector(`${this.SELECTORS.rowsContainer}[data-box="${box}"]`);
  }
}

customElements.define('flavormaxx-subscription', FlavormaxxSubscription);
